import { Core } from "@faire/design-tokens";
import { Button } from "@faire/web--source/slate/Button";
import { Input } from "@faire/web--source/slate/Input";
import { Column, Row } from "@faire/web--source/slate/Layout";
import { getSpacing, Spacer } from "@faire/web--source/slate/spacing";
import { Toggle } from "@faire/web--source/slate/Toggle";
import { Typography } from "@faire/web--source/slate/Typography";
import Fuse from "fuse.js";
import React from "react";
import styled from "styled-components";

import { ISearchableNode } from "../../types";

interface IConfigurableOptionsProps {
  searchOptions: Fuse.IFuseOptions<ISearchableNode>;
  setSearchOptions: React.Dispatch<
    React.SetStateAction<Fuse.IFuseOptions<ISearchableNode>>
  >;
}

export const ConfigurableOptions: React.FC<IConfigurableOptionsProps> = ({
  searchOptions,
  setSearchOptions,
}) => {
  return (
    <ConfigurableOptionsWrapper>
      <Row>
        <Typography variant="paragraphSansMedium">
          Search Configuration
        </Typography>
        <Spacer width="2x" />
        <Button
          variant="tertiary"
          linkTo="https://www.fusejs.io/api/options.html#options"
          target="__blank"
        >
          Trying to understand these options?
        </Button>
      </Row>
      <Spacer height="2x" />
      <Row>
        <Typography>keys: {JSON.stringify(searchOptions.keys)}</Typography>
      </Row>
      <Spacer height="1x" />
      <Row>
        <Typography color={Core.text.critical}>isCaseSensitive: </Typography>
        <Spacer width="1x" />
        <Toggle
          onChange={() =>
            setSearchOptions({
              ...searchOptions,
              isCaseSensitive: !searchOptions.isCaseSensitive,
            })
          }
          value={searchOptions.isCaseSensitive ?? false}
        />
        <Spacer width="2x" />
        <Typography color={Core.text.critical}>shouldSort: </Typography>
        <Spacer width="1x" />
        <Toggle
          onChange={() =>
            setSearchOptions({
              ...searchOptions,
              shouldSort: !searchOptions.shouldSort,
            })
          }
          value={searchOptions.shouldSort ?? false}
        />
        <Spacer width="1x" />
        <Typography color={Core.text.critical}>includeMatches: </Typography>
        <Spacer width="1x" />
        <Toggle
          onChange={() =>
            setSearchOptions({
              ...searchOptions,
              includeMatches: !searchOptions.includeMatches,
            })
          }
          value={searchOptions.includeMatches ?? false}
        />
      </Row>
      <Spacer width="1x" />
      <Row>
        <Typography color={Core.text.critical}>includeScore: </Typography>
        <Spacer width="1x" />
        <Toggle
          onChange={() =>
            setSearchOptions({
              ...searchOptions,
              includeScore: !searchOptions.includeScore,
            })
          }
          value={searchOptions.includeScore ?? false}
        />
        <Spacer width="2x" />
        <Typography color={Core.text.critical}>findAllMatches: </Typography>
        <Spacer width="1x" />
        <Toggle
          onChange={() =>
            setSearchOptions({
              ...searchOptions,
              findAllMatches: !searchOptions.findAllMatches,
            })
          }
          value={searchOptions.findAllMatches ?? false}
        />
        <Spacer width="2x" />
        <Typography color={Core.text.critical}>useExtendedSearch: </Typography>
        <Spacer width="1x" />
        <Toggle
          onChange={() =>
            setSearchOptions({
              ...searchOptions,
              useExtendedSearch: !searchOptions.useExtendedSearch,
            })
          }
          value={searchOptions.useExtendedSearch ?? false}
        />
      </Row>
      <Spacer width="2x" />
      <Row>
        <Input
          label="minMatchCharLength"
          placeholder="1"
          value={searchOptions.minMatchCharLength}
          onChange={(e) =>
            setSearchOptions({
              ...searchOptions,
              minMatchCharLength: Number(e.target.value),
            })
          }
        />
        <Spacer width="2x" />
        <Input
          label="location"
          placeholder="0"
          value={searchOptions.location}
          onChange={(e) =>
            setSearchOptions({
              ...searchOptions,
              location: Number(e.target.value),
            })
          }
        />
      </Row>
      <Spacer width="1x" />
      <Row>
        <Input
          type="number"
          label="threshold"
          placeholder="0.6"
          value={searchOptions.threshold}
          onChange={(e) =>
            setSearchOptions({
              ...searchOptions,
              threshold: parseFloat(e.target.value),
            })
          }
        />
        <Spacer width="2x" />
        <Input
          label="distance"
          placeholder="100"
          value={searchOptions.distance}
          onChange={(e) =>
            setSearchOptions({
              ...searchOptions,
              distance: Number(e.target.value),
            })
          }
        />
      </Row>
    </ConfigurableOptionsWrapper>
  );
};

const ConfigurableOptionsWrapper = styled(Column).attrs({
  justify: "center",
})`
  border: 1px solid ${Core.border.info};
  padding: ${getSpacing("2x")};
  text-wrap: wrap;
`;
